import React, { useCallback, useEffect, useRef, useState } from 'react';
import api, { API_BASE } from '../api';
import { useAuth } from '../context/AuthContext';
import EmojiTextarea from '../components/EmojiTextarea';

const API_ORIGIN = API_BASE.replace(/\/api$/, '');

const POLL_INTERVAL_MS = 4000;
const THEME_STORAGE_KEY = 'chat-theme';

const PATTERNS = [
  { key: 'none', label: '기본', icon: '📄' },
  { key: 'leaves', label: '단풍잎', icon: '🍁' },
  { key: 'flowers', label: '꽃무늬', icon: '🌸' },
  { key: 'river', label: '강물풍경', icon: '🏞️' },
];

const DEFAULT_THEME = { pattern: 'none', bg: '#fdf8ee', text: '#5b4a37', bubble: '#ffe9d6' };

function loadTheme() {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    if (!raw) return DEFAULT_THEME;
    return { ...DEFAULT_THEME, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_THEME;
  }
}

function saveTheme(theme) {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(theme));
  } catch {
    // 저장 실패해도 화면 사용에는 지장 없음 (사생활 보호 모드 등)
  }
}

function formatTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

function formatDay(iso) {
  if (!iso) return '';
  return iso.slice(0, 10);
}

export default function Chat() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [theme, setTheme] = useState(loadTheme);
  const [themeOpen, setThemeOpen] = useState(false);
  const [openComments, setOpenComments] = useState({});
  const [commentsByMsg, setCommentsByMsg] = useState({});
  const [commentDrafts, setCommentDrafts] = useState({});
  const [commentSending, setCommentSending] = useState({});
  const listRef = useRef(null);
  const themePanelRef = useRef(null);
  const lastIdRef = useRef(0);

  useEffect(() => {
    if (!themeOpen) return undefined;
    const handleOutside = (e) => {
      if (themePanelRef.current && !themePanelRef.current.contains(e.target)) setThemeOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [themeOpen]);

  const updateTheme = (patch) => {
    setTheme((prev) => {
      const next = { ...prev, ...patch };
      saveTheme(next);
      return next;
    });
  };

  const resetTheme = () => {
    setTheme(DEFAULT_THEME);
    saveTheme(DEFAULT_THEME);
  };

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    });
  };

  const loadInitial = useCallback(async () => {
    const res = await api.get('/chat/messages');
    setMessages(res.data);
    lastIdRef.current = res.data.length > 0 ? res.data[res.data.length - 1].msgid : 0;
    scrollToBottom();
  }, []);

  const pollNew = useCallback(async () => {
    const res = await api.get('/chat/messages', { params: { after_id: lastIdRef.current } });
    if (res.data.length > 0) {
      setMessages((prev) => [...prev, ...res.data]);
      lastIdRef.current = res.data[res.data.length - 1].msgid;
      scrollToBottom();
    }
  }, []);

  useEffect(() => {
    loadInitial();
    const timer = setInterval(pollNew, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [loadInitial, pollNew]);

  useEffect(() => {
    // 화면이 꺼졌다 켜지거나 다른 앱에 갔다가 돌아왔을 때 바로 최신 내용을 가져온다
    const handleVisible = () => {
      if (document.visibilityState === 'visible') pollNew();
    };
    document.addEventListener('visibilitychange', handleVisible);
    window.addEventListener('focus', handleVisible);
    return () => {
      document.removeEventListener('visibilitychange', handleVisible);
      window.removeEventListener('focus', handleVisible);
    };
  }, [pollNew]);

  const sendMessage = async () => {
    const content = text.trim();
    if (!content || sending) return;
    setSending(true);
    try {
      await api.post('/chat/messages', { content });
      setText('');
      await pollNew();
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const deleteMessage = async (msgid) => {
    await api.delete(`/chat/messages/${msgid}`);
    setMessages((prev) => prev.filter((m) => m.msgid !== msgid));
  };

  const toggleReaction = async (msgid, emoji) => {
    // 낙관적으로 먼저 화면을 바꾸고, 서버 응답으로 다시 맞춘다
    setMessages((prev) =>
      prev.map((m) => {
        if (m.msgid !== msgid) return m;
        return {
          ...m,
          reactions: m.reactions.map((r) =>
            r.emoji === emoji ? { ...r, reacted: !r.reacted, count: r.count + (r.reacted ? -1 : 1) } : r
          ),
        };
      })
    );
    try {
      const res = await api.post(`/chat/messages/${msgid}/reactions`, { emoji });
      const updated = res.data;
      setMessages((prev) =>
        prev.map((m) =>
          m.msgid !== msgid
            ? m
            : { ...m, reactions: m.reactions.map((r) => (r.emoji === emoji ? updated : r)) }
        )
      );
    } catch {
      await pollNew();
    }
  };

  const toggleComments = async (msgid) => {
    const willOpen = !openComments[msgid];
    setOpenComments((prev) => ({ ...prev, [msgid]: willOpen }));
    if (willOpen && !commentsByMsg[msgid]) {
      const res = await api.get(`/chat/messages/${msgid}/comments`);
      setCommentsByMsg((prev) => ({ ...prev, [msgid]: res.data }));
    }
  };

  const submitComment = async (msgid) => {
    const content = (commentDrafts[msgid] || '').trim();
    if (!content || commentSending[msgid]) return;
    setCommentSending((prev) => ({ ...prev, [msgid]: true }));
    try {
      const res = await api.post(`/chat/messages/${msgid}/comments`, { content });
      setCommentsByMsg((prev) => ({ ...prev, [msgid]: [...(prev[msgid] || []), res.data] }));
      setCommentDrafts((prev) => ({ ...prev, [msgid]: '' }));
      setMessages((prev) =>
        prev.map((m) => (m.msgid === msgid ? { ...m, comment_count: m.comment_count + 1 } : m))
      );
    } finally {
      setCommentSending((prev) => ({ ...prev, [msgid]: false }));
    }
  };

  const deleteComment = async (msgid, commentid) => {
    await api.delete(`/chat/comments/${commentid}`);
    setCommentsByMsg((prev) => ({
      ...prev,
      [msgid]: (prev[msgid] || []).filter((c) => c.commentid !== commentid),
    }));
    setMessages((prev) =>
      prev.map((m) => (m.msgid === msgid ? { ...m, comment_count: Math.max(0, m.comment_count - 1) } : m))
    );
  };

  let lastDay = '';

  return (
    <div className="page chat-page">
      <div className="page-header chat-header-row">
        <h2>항공기술교육원 교사 채팅방</h2>
        <div className="chat-theme-toggle-wrap" ref={themePanelRef}>
          <button className="chat-theme-btn" onClick={() => setThemeOpen((v) => !v)}>
            🎨 배경 꾸미기
          </button>
          {themeOpen && (
            <div className="chat-theme-panel">
              <div className="chat-theme-section-label">배경 무늬</div>
              <div className="chat-theme-patterns">
                {PATTERNS.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    className={`chat-theme-pattern-btn${theme.pattern === p.key ? ' active' : ''}`}
                    title={p.label}
                    onClick={() => updateTheme({ pattern: p.key })}
                  >
                    {p.icon}
                  </button>
                ))}
              </div>

              <div className="chat-theme-section-label">색상</div>
              <div className="chat-theme-color-row">
                <span>배경색</span>
                <input type="color" value={theme.bg} onChange={(e) => updateTheme({ bg: e.target.value })} />
              </div>
              <div className="chat-theme-color-row">
                <span>글자색</span>
                <input type="color" value={theme.text} onChange={(e) => updateTheme({ text: e.target.value })} />
              </div>
              <div className="chat-theme-color-row">
                <span>내 말풍선색</span>
                <input type="color" value={theme.bubble} onChange={(e) => updateTheme({ bubble: e.target.value })} />
              </div>

              <button type="button" className="chat-theme-reset-btn" onClick={resetTheme}>
                기본값으로 되돌리기
              </button>
            </div>
          )}
        </div>
      </div>

      <div
        className={`card chat-card chat-pattern-${theme.pattern}`}
        style={{ '--chat-bg': theme.bg, '--chat-text': theme.text, '--chat-bubble': theme.bubble }}
      >
        <div className="chat-list" ref={listRef}>
          {messages.map((m) => {
            const day = formatDay(m.createat);
            const showDay = day !== lastDay;
            lastDay = day;
            const mine = m.teachid === user?.teachid;
            return (
              <React.Fragment key={m.msgid}>
                {showDay && <div className="chat-day-divider">{day}</div>}
                <div className={`chat-msg-row${mine ? ' mine' : ''}`}>
                  <div className="chat-msg-avatar" title={m.author_name}>
                    {m.author_avatar_photo_url ? (
                      <img src={`${API_ORIGIN}${m.author_avatar_photo_url}`} alt="" className="chat-msg-avatar-img" />
                    ) : (
                      m.author_avatar || '🙂'
                    )}
                  </div>
                  <div className="chat-msg-bubble-wrap">
                    <div className="chat-msg-author">{m.author_name}</div>
                    <div className="chat-msg-bubble">{m.content}</div>
                    <div className="chat-reaction-row">
                      {m.reactions.map((r) => (
                        <button
                          key={r.emoji}
                          className={`chat-reaction-btn${r.reacted ? ' active' : ''}`}
                          title={r.label}
                          onClick={() => toggleReaction(m.msgid, r.emoji)}
                        >
                          {r.emoji}
                          {r.count > 0 && <span className="chat-reaction-count">{r.count}</span>}
                        </button>
                      ))}
                    </div>
                    <div className="chat-msg-meta">
                      {formatTime(m.createat)}
                      <button className="chat-msg-comment-toggle" onClick={() => toggleComments(m.msgid)}>
                        💬 댓글{m.comment_count > 0 ? ` ${m.comment_count}` : ''}
                      </button>
                      {mine && (
                        <button className="chat-msg-delete" onClick={() => deleteMessage(m.msgid)} title="삭제">✕</button>
                      )}
                    </div>
                    {openComments[m.msgid] && (
                      <div className="chat-comment-thread">
                        {(commentsByMsg[m.msgid] || []).map((c) => (
                          <div key={c.commentid} className="chat-comment-item">
                            <div className="chat-comment-avatar" title={c.author_name}>
                              {c.author_avatar_photo_url ? (
                                <img src={`${API_ORIGIN}${c.author_avatar_photo_url}`} alt="" className="chat-msg-avatar-img" />
                              ) : (
                                c.author_avatar || '🙂'
                              )}
                            </div>
                            <div className="chat-comment-body">
                              <div className="chat-comment-head">
                                <span className="chat-comment-author">{c.author_name}</span>
                                <span className="chat-comment-time">{formatTime(c.createat)}</span>
                                {(c.teachid === user?.teachid || user?.role === 'admin' || user?.role === 'staff') && (
                                  <button
                                    className="chat-msg-delete"
                                    onClick={() => deleteComment(m.msgid, c.commentid)}
                                    title="삭제"
                                  >
                                    ✕
                                  </button>
                                )}
                              </div>
                              <div className="chat-comment-content">{c.content}</div>
                            </div>
                          </div>
                        ))}
                        {(commentsByMsg[m.msgid] || []).length === 0 && (
                          <div className="chat-comment-empty">첫 댓글을 남겨보세요.</div>
                        )}
                        <div className="chat-comment-input-row">
                          <input
                            type="text"
                            className="chat-comment-input"
                            placeholder="댓글을 입력하세요"
                            value={commentDrafts[m.msgid] || ''}
                            onChange={(e) =>
                              setCommentDrafts((prev) => ({ ...prev, [m.msgid]: e.target.value }))
                            }
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                submitComment(m.msgid);
                              }
                            }}
                          />
                          <button
                            className="btn-ghost chat-comment-send-btn"
                            onClick={() => submitComment(m.msgid)}
                            disabled={commentSending[m.msgid] || !(commentDrafts[m.msgid] || '').trim()}
                          >
                            등록
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </React.Fragment>
            );
          })}
          {messages.length === 0 && <div className="chat-empty">아직 메시지가 없습니다. 첫 메시지를 남겨보세요.</div>}
        </div>

        <div className="chat-input-row">
          <EmojiTextarea
            value={text}
            onChange={setText}
            onKeyDown={handleKeyDown}
            placeholder="메시지를 입력하세요 (Enter로 전송, Shift+Enter로 줄바꿈)"
            rows={2}
          />
          <button className="btn-primary btn-inline chat-send-btn" onClick={sendMessage} disabled={sending || !text.trim()}>
            전송
          </button>
        </div>
      </div>
    </div>
  );
}
